import { DIALOG_DATA, Dialog, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, Injectable, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ButtonComponent } from '../button/button.component';
import { CodeInputComponent } from '../code-input/code-input.component';
import { AlertComponent } from '../feedback/alert.component';
import { FieldComponent } from '../form/field.component';
import { uniqueId } from '../form/ids';
import { PasswordFieldComponent } from '../form/password-field.component';

export interface ReauthDialogData {
  /** La verifica in due passaggi è attiva: oltre alla password serve il codice. */
  readonly totpEnabled: boolean;
  /** Il dispositivo sa usare le passkey: compare "Usa una passkey". */
  readonly passkeySupported: boolean;
}

/** Ciò che l'utente ha scelto per confermare l'identità. */
export type ReauthInput =
  | {
      readonly kind: 'password';
      readonly password: string;
      readonly code?: string;
      readonly recoveryCode?: string;
    }
  | { readonly kind: 'passkey' };

/**
 * Verifica la scelta (di solito con `POST /api/auth/reauthenticate`): restituisce il messaggio
 * d'errore da mostrare, in italiano, oppure `null` se l'identità è confermata.
 */
export type ReauthSubmit = (input: ReauthInput) => Promise<string | null>;

interface ReauthDialogConfig extends ReauthDialogData {
  readonly id: string;
  readonly submit: ReauthSubmit;
}

/**
 * Finestra "Conferma la tua identità" (research R-10), su CDK Dialog come la conferma: password
 * più codice dell'app (o un codice di recupero) se la verifica in due passaggi è attiva, oppure
 * una passkey. Non chiama l'API: passa la scelta alla funzione `submit` ricevuta e mostra
 * l'errore che restituisce. Si apre con `ReauthDialogService.open()`.
 */
@Component({
  selector: 'bs-reauth-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    AlertComponent,
    ButtonComponent,
    CodeInputComponent,
    FieldComponent,
    PasswordFieldComponent,
  ],
  host: { class: 'block' },
  template: `
    <form
      class="flex max-h-dvh w-full max-w-md flex-col gap-4 overflow-y-auto rounded-3xl border border-stone-200 bg-white p-6 shadow-lg dark:border-stone-700 dark:bg-stone-900"
      data-testid="reauth-dialog"
      novalidate
      (submit)="confirm($event)"
    >
      <div class="flex flex-col gap-2">
        <h2 [id]="titleId" class="text-xl font-semibold text-ink dark:text-stone-50">
          Conferma la tua identità
        </h2>
        <p [id]="messageId" class="text-sm text-stone-600 dark:text-stone-300">
          @if (data.passkeySupported) {
            Per sicurezza, prima di continuare usa una passkey oppure inserisci di nuovo la
            password.
          } @else {
            Per sicurezza, prima di continuare inserisci di nuovo la password.
          }
        </p>
      </div>

      @if (error(); as message) {
        <bs-alert variant="error" testId="reauth-error">{{ message }}</bs-alert>
      }

      <bs-password-field
        label="Password"
        autocomplete="current-password"
        testId="reauth-password"
        [formControl]="password"
        [error]="passwordError()"
      />

      @if (data.totpEnabled) {
        @if (useRecoveryCode()) {
          <bs-field
            label="Codice di recupero"
            testId="reauth-recovery-code"
            autocomplete="off"
            [mono]="true"
            [formControl]="recoveryCode"
            [error]="codeError()"
          />
        } @else {
          <bs-code-input
            label="Codice dell'app di autenticazione"
            testId="reauth-code"
            [value]="code()"
            (valueChange)="code.set($event)"
            [error]="codeError()"
          />
        }
        <div>
          <bs-button variant="ghost" testId="reauth-toggle-recovery" (click)="toggleRecovery()">
            {{ useRecoveryCode() ? "Usa il codice dell'app" : 'Usa un codice di recupero' }}
          </bs-button>
        </div>
      }

      <div class="mt-2 flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
        <bs-button
          variant="secondary"
          testId="reauth-cancel"
          [disabled]="busy() !== null"
          (click)="cancel()"
        >
          Annulla
        </bs-button>
        @if (data.passkeySupported) {
          <bs-button
            variant="secondary"
            icon="key"
            testId="reauth-passkey"
            [loading]="busy() === 'passkey'"
            [disabled]="busy() === 'password'"
            (click)="usePasskey()"
          >
            Usa una passkey
          </bs-button>
        }
        <bs-button
          type="submit"
          testId="reauth-confirm"
          [loading]="busy() === 'password'"
          [disabled]="busy() === 'passkey'"
        >
          Conferma
        </bs-button>
      </div>
    </form>
  `,
})
export class ReauthDialogComponent {
  protected readonly data = inject<ReauthDialogConfig>(DIALOG_DATA);
  private readonly ref = inject<DialogRef<boolean>>(DialogRef);
  protected readonly titleId = `${this.data.id}-title`;
  protected readonly messageId = `${this.data.id}-message`;

  protected readonly password = new FormControl('', { nonNullable: true });
  protected readonly recoveryCode = new FormControl('', { nonNullable: true });
  protected readonly code = signal('');
  protected readonly useRecoveryCode = signal(false);
  protected readonly busy = signal<'password' | 'passkey' | null>(null);
  protected readonly error = signal<string | null>(null);
  protected readonly passwordError = signal<string | null>(null);
  protected readonly codeError = signal<string | null>(null);

  protected toggleRecovery(): void {
    this.useRecoveryCode.set(!this.useRecoveryCode());
    this.codeError.set(null);
  }

  protected cancel(): void {
    this.ref.close(false);
  }

  protected async confirm(event: Event): Promise<void> {
    event.preventDefault();
    if (this.busy()) {
      return;
    }
    const input = this.validate();
    if (input) {
      await this.run('password', input);
    }
  }

  protected async usePasskey(): Promise<void> {
    if (!this.busy()) {
      this.passwordError.set(null);
      this.codeError.set(null);
      await this.run('passkey', { kind: 'passkey' });
    }
  }

  /** Controlla i campi e prepara la richiesta; `null` se manca qualcosa. */
  private validate(): ReauthInput | null {
    const password = this.password.value;
    this.passwordError.set(password ? null : 'Inserisci la password.');
    let code: string | undefined;
    let recoveryCode: string | undefined;
    this.codeError.set(null);
    if (this.data.totpEnabled) {
      if (this.useRecoveryCode()) {
        recoveryCode = this.recoveryCode.value.trim();
        if (!recoveryCode) this.codeError.set('Inserisci un codice di recupero.');
      } else {
        code = this.code();
        if (code.length !== 6) this.codeError.set('Inserisci il codice di 6 cifre.');
      }
    }
    if (this.passwordError() || this.codeError()) {
      return null;
    }
    return { kind: 'password', password, code, recoveryCode };
  }

  private async run(kind: 'password' | 'passkey', input: ReauthInput): Promise<void> {
    this.busy.set(kind);
    this.error.set(null);
    this.ref.disableClose = true;
    let message: string | null;
    try {
      message = await this.data.submit(input);
    } catch {
      message = 'Non è stato possibile confermare la tua identità. Riprova.';
    }
    this.ref.disableClose = false;
    this.busy.set(null);
    if (message === null) {
      this.ref.close(true);
    } else {
      this.error.set(message);
    }
  }
}

@Injectable({ providedIn: 'root' })
export class ReauthDialogService {
  private readonly dialog = inject(Dialog);

  /**
   * Apre la conferma dell'identità. `submit` verifica la scelta dell'utente; la promessa vale
   * `true` solo quando `submit` ha confermato l'identità, `false` se si annulla.
   */
  async open(data: ReauthDialogData, submit: ReauthSubmit): Promise<boolean> {
    const id = uniqueId('bs-reauth');
    const ref = this.dialog.open<boolean, ReauthDialogConfig, ReauthDialogComponent>(
      ReauthDialogComponent,
      {
        data: { ...data, id, submit },
        ariaLabelledBy: `${id}-title`,
        ariaDescribedBy: `${id}-message`,
        role: 'dialog',
        ariaModal: true,
        autoFocus: 'first-tabbable',
        restoreFocus: true,
        hasBackdrop: true,
        backdropClass: ['bg-stone-950/50'],
        panelClass: ['w-full', 'max-w-md', 'px-4'],
      },
    );
    const result = await firstValueFrom(ref.closed, { defaultValue: undefined });
    return result === true;
  }
}
